// frontend/src/lib/wsDebugLog.js
//
// Shared debug logger for the WS reconnect instrumentation (Fase A/B del plan
// docs/superpowers/plans/2026-05-22-ws-reconnect-hardening.md).
//
// Además de console.warn (sobrevive el build de prod — ver PR #458), cada
// entrada se persiste en un ring buffer de localStorage para que la evidencia
// sobreviva al clear de la consola / cierre de la pestaña. Dump manual desde
// DevTools: `window.__wsDebugDump()` (texto) o `window.__wsDebugEntries()`.
//
// El gate tiene DOS vías:
//   - build: VITE_WS_DEBUG=1 (como antes).
//   - runtime: `window.__wsDebugEnable()` en la consola, que persiste el flag
//     en localStorage y sobrevive recargas.
// La vía runtime existe para poder instrumentar un fallo en PRODUCCIÓN sin
// recompilar ni redeployar: se prende, se reproduce el fallo, se hace dump.
// Apagado por defecto — sin flag no hay logging ni escrituras a localStorage.

const BUILD_FLAG = import.meta.env.VITE_WS_DEBUG === "1";
const STORAGE_KEY = "ws_debug_log_v1";
const ENABLED_KEY = "ws_debug_enabled_v1";
const MAX_ENTRIES = 400;

function readEnabledFlag() {
  if (BUILD_FLAG) return true;
  try {
    return window.localStorage.getItem(ENABLED_KEY) === "1";
  } catch {
    // localStorage bloqueado (Safari private) o entorno sin window.
    return false;
  }
}

// Cacheado en memoria: isEnabled() se consulta en cada log y hay ~200
// instancias de VehicleMotion, así que no queremos pegarle a localStorage cada
// vez. setWsDebug() mantiene ambos en sincronía.
let enabled = readEnabledFlag();

export function isWsDebugEnabled() {
  return enabled;
}

/** Prende/apaga el logging en runtime y lo persiste para las próximas cargas. */
export function setWsDebug(on) {
  enabled = !!on || BUILD_FLAG;
  try {
    if (on) window.localStorage.setItem(ENABLED_KEY, "1");
    else window.localStorage.removeItem(ENABLED_KEY);
  } catch {
    // ignore — el flag en memoria igual quedó aplicado para esta sesión.
  }
  return enabled;
}

function persist(tag, msg, ctx) {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const entries = raw ? JSON.parse(raw) : [];
    entries.push({
      ts: new Date().toISOString(),
      tag,
      msg,
      // ctx puede traer Errors u objetos no serializables — degradar a string.
      ctx: ctx === undefined ? null : JSON.parse(JSON.stringify(ctx, (_k, v) => (v instanceof Error ? String(v) : v))),
    });
    if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota llena, localStorage bloqueado (Safari private), ctx circular…
    // el logging nunca debe romper la app.
  }
}

export function readWsDebugEntries() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function clearWsDebugEntries() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Crea un logger etiquetado, gated por el flag de build O el de runtime.
 * Misma firma que los wsLog previos: (msg, ctx?) => void.
 */
export function createWsLogger(tag) {
  return (msg, ctx) => {
    if (!enabled) return;
    console.warn(tag, msg, ctx ?? "");
    persist(tag, msg, ctx);
  };
}

if (typeof window !== "undefined") {
  window.__wsDebugEntries = readWsDebugEntries;
  window.__wsDebugDump = () =>
    readWsDebugEntries()
      .map((e) => `${e.ts} ${e.tag} ${e.msg} ${e.ctx ? JSON.stringify(e.ctx) : ""}`)
      .join("\n");
  window.__wsDebugClear = clearWsDebugEntries;
  window.__wsDebugEnable = () => {
    clearWsDebugEntries();
    setWsDebug(true);
    return "ws debug ON — reproducí el fallo y después ejecutá __wsDebugDump()";
  };
  window.__wsDebugDisable = () => {
    setWsDebug(false);
    return "ws debug OFF";
  };
}
